import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { coursesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LegalDisclaimer from '../components/LegalDisclaimer';
import CourseHero from './courses/CourseHero';
import CourseGenerator from './courses/CourseGenerator';
import SavedCoursesList from './courses/SavedCoursesList';

const Courses = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [quota, setQuota] = useState(null);
  const [courses, setCourses] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [q, list] = await Promise.all([
        coursesAPI.quota(),
        coursesAPI.list(),
      ]);
      setQuota(q);
      setCourses(list);
    } catch (e) {
      if (e.response?.status === 401) {
        // not logged in — leave loaded=true, render gated CTA below
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load courses');
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) refresh();
  }, [authLoading, refresh]);

  const handleGenerate = async (payload) => {
    setGenerating(true);
    try {
      const result = await coursesAPI.generate(payload);
      toast.success('Course generated!');
      // Refresh list + quota, then navigate to the new course detail.
      await refresh();
      navigate(`/courses/${result.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Failed to generate course';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await coursesAPI.remove(id);
      toast.success('Course deleted');
      setCourses((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            AI Course Creator
          </h1>
          <p className="mt-3 text-gray-600">
            Sign in to generate your free monthly investing course.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="mt-6 px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium"
            data-testid="courses-signin-cta"
          >
            Sign in to continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <CourseHero quota={quota} />

        <CourseGenerator
          onGenerate={handleGenerate}
          generating={generating}
          quotaRemaining={quota?.tier === 'pro' ? null : quota?.remaining}
        />

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Your courses
          </h2>
          {loaded ? (
            <SavedCoursesList courses={courses} onDelete={handleDelete} />
          ) : (
            <p className="text-sm text-gray-500">Loading…</p>
          )}
        </div>

        <LegalDisclaimer variant="general" />
      </div>
    </div>
  );
};

export default Courses;
